import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import {
  MdCheckCircle,
  MdCancel,
  MdVisibility,
  MdChat,
} from "react-icons/md";
import toast from "react-hot-toast";
import moment from "moment";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import WithdrawalMangementServices from "../../services/WithdrawalMangementServices";
import { AuthContext } from "../../context/AuthContext";
import QuickChatModal from "../../components/QuickChatModal";
import WithdrawalActionModal from "./modals/WithdrawalActionModal";
import WithdrawalDetailsModal from "./modals/WithdrawalDetailsModal";
import ExportMenu from "../../components/ExportMenu";
import "../../styles/withdrawalManage.css";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

const PAGE_SIZES = [10, 25, 50, 100];

const money = (v) => Number(v || 0).toFixed(2);

/** Shared by the Excel and PDF export so the two can never drift apart. */
const EXPORT_COLUMNS = [
  { header: "Sr.", value: (r, i) => i + 1 },
  { header: "Player", value: (r) => r.user_name || "-" },
  { header: "Game ID", value: (r) => r.game_id || "-" },
  { header: "Mobile", value: (r) => r.mobile || "-" },
  { header: "Amount", value: (r) => Number(r.amount || 0) },
  { header: "Fee (locked in wallet)", value: (r) => Number(r.fee_amount || 0) },
  { header: "Fee %", value: (r) => Number(r.fee_percentage || 0) },
  // Payout is the bank transfer and the wallet movement both - the fee is
  // never credited to anyone, so it must not be read as income in an export.
  { header: "Payout (bank + wallet)", value: (r) => Number(r.payout_amount || 0) },
  { header: "Bank", value: (r) => r.bank_name || "-" },
  { header: "Holder", value: (r) => r.account_holder_name || "-" },
  // Forced to text so Excel cannot mangle a long account number into
  // scientific notation.
  { header: "Account No.", value: (r) => `${r.account_number || "-"}` },
  { header: "IFSC", value: (r) => r.ifsc_code || "-" },
  { header: "UPI ID", value: (r) => r.upi_id || "-" },
  { header: "Reference", value: (r) => r.reference_id || "-" },
  { header: "Status", value: (r) => r.status },
  {
    header: "Requested",
    value: (r) =>
      r.created_at ? moment(r.created_at).format("DD/MM/YYYY hh:mm A") : "-",
  },
  {
    header: "Processed",
    value: (r) =>
      r.processed_at ? moment(r.processed_at).format("DD/MM/YYYY hh:mm A") : "-",
  },
  { header: "Processed By", value: (r) => r.processed_by_name || "-" },
  { header: "Remark", value: (r) => r.admin_remark || "-" },
];

const WithdrawalManagement = () => {
  const withdrawalApi = new WithdrawalMangementServices();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [actionModal, setActionModal] = useState(null); // {row, mode}
  const [chatRow, setChatRow] = useState(null); // pending row being messaged
  const [detailRow, setDetailRow] = useState(null);

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await withdrawalApi.getWithdrawalList(
        page,
        limit,
        searchFilter,
        status,
        fromDate,
        toDate,
      );
      if (response.isSuccess) {
        setRows(response.data || []);
        setPagination(response.pagination || null);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to load withdrawal requests");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, searchFilter, fromDate, toDate]);

  useEffect(() => {
    getList();
  }, [getList]);

  // Kept in a ref so the live-refresh listener below always calls the current
  // version without re-subscribing on every filter change.
  const getListRef = useRef(getList);
  useEffect(() => {
    getListRef.current = getList;
  }, [getList]);

  /**
   * TopBar owns the socket and re-broadcasts new requests as a window event,
   * so the queue refreshes itself without a second connection or a duplicate
   * toast - same pattern as the deposit screen.
   */
  useEffect(() => {
    const onNew = () => getListRef.current();
    window.addEventListener("yantra:newWithdrawalRequest", onNew);
    return () => window.removeEventListener("yantra:newWithdrawalRequest", onNew);
  }, []);

  const statusBadge = (s) => (
    <span className={`wd-badge wd-badge-${String(s).toLowerCase()}`}>{s}</span>
  );

  const clearDates = () => {
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  return (
    <div className="withdrawal-page">
      <div className="wd-header">
        <div>
          <h2 className="wd-title">Withdrawal Requests</h2>
          <p className="wd-subtitle">
            {Number(user?.role_id) === 1
              ? "All withdrawal requests across the platform."
              : "Withdrawal requests from your mobile users."}{" "}
            Approving debits the payout from the player and credits the same
            figure to your wallet — transfer exactly that to their bank. The fee
            stays locked in the player's wallet and is credited to no one.
          </p>
        </div>
      </div>

      <div className="wd-filters">
        <div className="wd-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key || "ALL"}
              className={`wd-tab ${status === tab.key ? "active" : ""}`}
              onClick={() => {
                setStatus(tab.key);
                setPage(1);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="wd-search">
          <CiSearch size={18} />
          <input
            type="text"
            placeholder="Search user, game ID, mobile, reference or account no."
            value={searchFilter}
            onChange={(e) => {
              setSearchFilter(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="wd-dates">
          <label>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
          />
          <label>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
          />
          {(fromDate || toDate) && (
            <button className="wd-clear" onClick={clearDates}>
              Clear
            </button>
          )}
        </div>

        <div className="page-size">
          <span>Show</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {/* Exports the whole filtered range, not just the page on screen. */}
        <ExportMenu
          fetchAll={() =>
            withdrawalApi.getWithdrawalListForExport({
              status,
              filter: searchFilter,
              fromDate,
              toDate,
            })
          }
          columns={EXPORT_COLUMNS}
          fileName={`withdrawal_requests_${fromDate || "all"}_to_${toDate || "all"}`}
          title="Withdrawal Requests"
          subtitle={
            fromDate || toDate
              ? `From ${fromDate || "start"} to ${toDate || "today"}${status ? ` | ${status}` : ""}`
              : status
                ? `Status: ${status}`
                : "All withdrawal requests"
          }
          disabled={loading}
        />
      </div>

      {loading ? (
        <Loader message="Loading withdrawal requests..." />
      ) : rows.length === 0 ? (
        <NoData
          message="No Withdrawal Requests"
          subText={
            status === "PENDING"
              ? "Nothing waiting for approval right now"
              : "Try a different status, date range or search"
          }
        />
      ) : (
        <div className="wd-table-wrapper">
          <table className="wd-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Amount</th>
                <th>Fee (locked)</th>
                <th>Payout</th>
                <th>Bank Account</th>
                <th>Reference</th>
                <th>Requested</th>
                <th>Status</th>
                <th className="text-end wd-col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>
                    <div className="wd-user">{row.user_name}</div>
                    <small className="wd-muted">
                      {row.game_id} · {row.mobile}
                    </small>
                  </td>
                  <td className="wd-amount">₹{money(row.amount)}</td>
                  <td>
                    {Number(row.fee_amount) > 0 ? (
                      <span
                        className="wd-fee"
                        title="Stays in the player's wallet as locked play credit — not credited to any admin"
                      >
                        −₹{money(row.fee_amount)}
                        <small> ({Number(row.fee_percentage)}%)</small>
                      </span>
                    ) : (
                      <span className="wd-muted">—</span>
                    )}
                  </td>
                  <td className="wd-payout">₹{money(row.payout_amount)}</td>
                  <td>
                    <div className="wd-bank">{row.bank_name || "—"}</div>
                    <small className="wd-muted wd-acc">
                      {row.account_number}
                      {row.ifsc_code ? ` · ${row.ifsc_code}` : ""}
                    </small>
                  </td>
                  <td className="wd-ref">{row.reference_id}</td>
                  <td className="wd-muted">
                    {moment(row.created_at).format("DD MMM YY, hh:mm A")}
                  </td>
                  <td>
                    {statusBadge(row.status)}
                    {row.admin_remark && (
                      <div className="wd-remark" title={row.admin_remark}>
                        {row.admin_remark}
                      </div>
                    )}
                  </td>
                  <td className="text-end wd-col-action">
                    <div className="wd-actions">
                      {row.status !== "PENDING" && (
                        <span
                          className="wd-muted wd-processed-by"
                          title={`Processed by ${row.processed_by_name || "—"}`}
                        >
                          {row.processed_by_name || "—"}
                        </span>
                      )}
                      <button
                        className="wd-btn wd-btn-view"
                        title="View full details"
                        onClick={() => setDetailRow(row)}
                      >
                        <MdVisibility size={17} />
                      </button>
                      {row.status === "PENDING" && (
                        <>
                          <button
                            className="wd-btn wd-btn-chat"
                            title="Message this player"
                            onClick={() => setChatRow(row)}
                          >
                            <MdChat size={16} />
                          </button>
                          <button
                            className="wd-btn wd-btn-approve"
                            title="Approve this withdrawal"
                            onClick={() =>
                              setActionModal({ row, mode: "approve" })
                            }
                          >
                            <MdCheckCircle size={18} />
                          </button>
                          <button
                            className="wd-btn wd-btn-reject"
                            title="Reject this withdrawal"
                            onClick={() => setActionModal({ row, mode: "reject" })}
                          >
                            <MdCancel size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="wd-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.totalPages} ·{" "}
            {pagination.total} total
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {detailRow && (
        <WithdrawalDetailsModal
          row={detailRow}
          onClose={() => setDetailRow(null)}
          onAct={(row, mode) => {
            // Hand off to the confirm step - approving moves money and should
            // never be a single click from a browsing screen.
            setDetailRow(null);
            setActionModal({ row, mode });
          }}
        />
      )}

      {chatRow && (
        <QuickChatModal
          player={{
            id: chatRow.user_id,
            name: chatRow.user_name,
            game_id: chatRow.game_id,
            mobile: chatRow.mobile,
          }}
          presets={[
            "Please confirm your bank account number and IFSC code.",
            "The account holder name does not match your profile. Please confirm.",
            "Your withdrawal is being processed and will reach your bank shortly.",
          ]}
          onClose={() => setChatRow(null)}
        />
      )}

      {actionModal && (
        <WithdrawalActionModal
          row={actionModal.row}
          mode={actionModal.mode}
          onClose={() => setActionModal(null)}
          onDone={() => {
            setActionModal(null);
            getList();
          }}
        />
      )}
    </div>
  );
};

export default WithdrawalManagement;
